import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logWithTimestamp, AuthEvent, logAuthEvent } from './authUtils';
import { getValidUserTypes } from './userTypeUtils';

/**
 * Checks if the current URL is an auth callback URL
 */
export function isAuthCallback() {
  const isCallback = window.location.hash.includes('access_token') || 
                     window.location.search.includes('code=');
  
  if (isCallback) {
    logWithTimestamp('Auth callback URL detected');
  }
  
  return isCallback;
}

/**
 * Processes auth callback data to ensure a valid user type is set
 * @param user The user object from the auth callback
 */
async function processCallbackUserData(user: any): Promise<void> {
  try {
    if (!user) return;
    
    // Extract user type from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const userTypeFromUrl = urlParams.get('user_type');
    
    // If no user_type in metadata but present in URL, store it
    if (!user.user_metadata?.user_type && userTypeFromUrl) {
      logWithTimestamp(`Setting user_type from URL param: ${userTypeFromUrl}`);
      
      // Validate user type
      const validTypes = await getValidUserTypes();
      if (validTypes.includes(userTypeFromUrl)) {
        // Update user metadata with the validated user type
        await supabase.auth.updateUser({
          data: { user_type: userTypeFromUrl }
        });
      }
    }
  } catch (error) {
    logWithTimestamp('Error processing callback user data:', error);
  }
}

/**
 * Handles the authentication callback after OAuth or email magic link
 * @returns Promise with the result of the callback handling
 */
export async function handleAuthCallback(): Promise<{ success: boolean; error?: any }> {
  try {
    logWithTimestamp('Handling auth callback');
    const hashParams = window.location.hash;
    
    // For OAuth callbacks, we might have URL data to process
    if (hashParams) {
      logWithTimestamp('Hash parameters found in URL');
    }

    // Using getSession() to get the most up-to-date session data
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      logWithTimestamp('Error getting session during auth callback:', error);
      return { success: false, error };
    }
    
    if (data.session?.user) {
      logWithTimestamp('Session found in auth callback, user:', data.session.user.email);
      
      // Process user data from callback
      await processCallbackUserData(data.session.user);
      
      return { success: true };
    } else {
      logWithTimestamp('No session found in auth callback');
      return { success: false, error: 'No session found' };
    }
  } catch (error) {
    logWithTimestamp('Unexpected error in auth callback:', error);
    return { success: false, error };
  }
}

/**
 * Redirects to a specific path after authentication
 * @param path Path to redirect to (defaults to /chat)
 */
export function redirectAfterAuth(path = '/chat') {
  logWithTimestamp(`Redirecting to ${path} after successful authentication setup`);
  window.location.href = path;
}
